import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import { AIR, FONT_MONO } from './theme'

export function SectionLabel({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: AIR.muted }}>
        {children}
      </span>
      {right}
    </div>
  )
}

export function KV({ k, v, mono = true }: { k: string; v: ReactNode; mono?: boolean }) {
  if (v === undefined || v === null || v === '') return null
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-[3px] text-[12px]">
      <span className="shrink-0" style={{ color: AIR.muted }}>
        {k}
      </span>
      <span className="min-w-0 truncate text-right" style={{ color: AIR.text, fontFamily: mono ? FONT_MONO : undefined }}>
        {v}
      </span>
    </div>
  )
}

export function Chip({ color, children, solid = false }: { color: string; children: ReactNode; solid?: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-sm px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wider"
      style={
        solid
          ? { background: color, color: AIR.bg }
          : { color, boxShadow: `inset 0 0 0 1px ${color}66`, background: `${color}14` }
      }
    >
      {children}
    </span>
  )
}

export function ActionButton({
  onClick, children, disabled, busy, tone = 'default', title,
}: {
  onClick?: () => void
  children: ReactNode
  disabled?: boolean
  busy?: boolean
  tone?: 'default' | 'primary'
  title?: string
}) {
  const primary = tone === 'primary'
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled || busy}
      className="inline-flex items-center justify-center gap-1.5 rounded-sm px-2.5 py-1.5 text-[11px] font-medium transition-colors focus-visible:outline focus-visible:outline-1 disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        background: primary ? AIR.accent : AIR.panel2,
        color: primary ? AIR.bg : AIR.text,
        boxShadow: primary ? undefined : `inset 0 0 0 1px ${AIR.lineStrong}`,
        outlineColor: AIR.accent,
      }}
    >
      {busy && <Loader2 className="h-3 w-3 animate-spin" />}
      {children}
    </button>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 py-2 text-[12px]" style={{ color: AIR.muted }}>
      {children}
    </div>
  )
}

export function errorText(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  try {
    const parsed = JSON.parse(msg)
    return parsed.detail || msg
  } catch {
    return msg
  }
}

/** POST /air-traffic/report/{kind} and save the PDF. */
export async function downloadReport(kind: 'area' | 'airport' | 'aircraft' | 'operator', body: Record<string, unknown>, name: string) {
  toast({ title: 'Building report', description: 'Collecting live data and writing the assessment (about 30 s).' })
  try {
    const blob = await apiClient.airReport(kind, body)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sycek_air_${kind}_${name.replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 5000)
    toast({ title: 'Report downloaded' })
  } catch (e) {
    toast({ title: 'Report failed', description: errorText(e), variant: 'destructive' })
  }
}
